/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import { Component, computed, EventEmitter, Input, OnDestroy, OnInit, Output, inject, signal, ChangeDetectionStrategy } from '@angular/core'
import { TranslateParser } from '@ngx-translate/core'
import { WINDOW } from '../../services/utils'

/**
 * Seconds at which the visually-hidden live region announces the remaining time
 * to screen-reader users. Keeps SR users informed without flooding them with a
 * tick every second.
 */
const ANNOUNCEMENT_MILESTONES = new Set<number>([30, 15, 5, 0])

@Component({
  selector: 'myprofile-signup-ui-timer-label',
  templateUrl: './timer-label.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TimerLabelComponent implements OnInit, OnDestroy {
  protected window = inject<Window>(WINDOW)
  private translateParser = inject(TranslateParser)

  @Input() waitingTimerLabelText!: string
  @Input() requestCodeTimerLabel!: string
  @Input() inactiveTimerButton!: boolean
  @Input() timer = 30

  @Output() clickedTimerEvent = new EventEmitter<void>()

  public isEnabled = signal(false)
  public currentTime = signal(30)
  public announcement = signal('')

  protected timerLabel = computed(() =>
    this.translateParser.interpolate(this.waitingTimerLabelText, { TIME_REMAINING: this.currentTime() }),
  )

  private counter: number | undefined

  ngOnInit() {
    this.currentTime.set(this.timer)
    this.start()
  }

  ngOnDestroy() {
    if (this.counter) {
      clearInterval(this.counter)
    }
  }

  public clickHandler() {
    this.isEnabled.set(false)
    this.announcement.set('')
    this.clickedTimerEvent.emit()
    this.currentTime.set(this.timer)
    this.start()
  }

  private start() {
    this.currentTime.update((v) => v - 1)
    this.maybeAnnounce()

    this.counter = this.window.setInterval(() => {
      this.currentTime.update((v) => v - 1)
      this.maybeAnnounce()

      if (this.currentTime() === -1) {
        clearInterval(this.counter)
        this.counter = undefined
        this.isEnabled.set(true)
        this.currentTime.set(this.timer)
      }
    }, 1000)
  }

  private maybeAnnounce() {
    const remaining = this.currentTime()
    if (!ANNOUNCEMENT_MILESTONES.has(remaining)) {
      return
    }
    this.announcement.set(this.translateParser.interpolate(this.waitingTimerLabelText, { TIME_REMAINING: remaining }) ?? '')
  }
}
